import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { X, Loader2, Send, Briefcase } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'artist', label: 'Artist' },
  { value: 'producer', label: 'Producer' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'studio', label: 'Studio' },
  { value: 'videographer', label: 'Videographer' },
  { value: 'graphic_designer', label: 'Graphic Designer' },
  { value: 'podcaster', label: 'Podcaster' },
  { value: 'streamer', label: 'Streamer' },
];

export default function GigFormModal({ onClose, onCreated, editGig }) {
  const { getAuthHeaders } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: editGig?.title || '',
    description: editGig?.description || '',
    roles_needed: editGig?.roles_needed || [],
    budget_min: editGig?.budget_min || '',
    budget_max: editGig?.budget_max || '',
    budget_type: editGig?.budget_type || 'negotiable',
    timeline: editGig?.timeline || '',
    location_city: editGig?.location_city || '',
    location_neighborhood: editGig?.location_neighborhood || '',
  });

  const toggleRole = (role) => {
    setForm(f => ({
      ...f,
      roles_needed: f.roles_needed.includes(role)
        ? f.roles_needed.filter(r => r !== role)
        : [...f.roles_needed, role]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.roles_needed.length === 0) { toast.error('Select at least one role'); return; }

    setSubmitting(true);
    try {
      const body = {
        ...form,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
      };
      const url = editGig ? `${API}/gigs/${editGig.gig_id}` : `${API}/gigs`;
      const method = editGig ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save gig');
      }
      toast.success(editGig ? 'Gig updated!' : 'Gig posted!');
      onCreated();
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="glass-card border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="gig-form-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold">{editGig ? 'Edit Gig' : 'Post a Gig'}</h2>
                <p className="text-xs text-muted-foreground">Find the right creator for your project</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-gig-form"><X className="w-5 h-5" /></Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Gig Title *</label>
              <Input
                placeholder="e.g., Need videographer for music video shoot"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                data-testid="gig-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Textarea
                placeholder="Describe the project, requirements, and expectations..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={4}
                data-testid="gig-desc-input"
              />
            </div>

            {/* Roles Needed */}
            <div>
              <label className="text-sm font-medium mb-2 block">Who are you hiring? *</label>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map(r => (
                  <Badge
                    key={r.value}
                    variant={form.roles_needed.includes(r.value) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-colors ${form.roles_needed.includes(r.value) ? 'bg-secondary text-secondary-foreground' : 'hover:bg-muted/50'}`}
                    onClick={() => toggleRole(r.value)}
                    data-testid={`role-toggle-${r.value}`}
                  >
                    {r.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Budget */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Budget (optional)</label>
              <div className="grid grid-cols-3 gap-3">
                <Input type="number" placeholder="Min $" value={form.budget_min} onChange={e => setForm(f => ({ ...f, budget_min: e.target.value }))} data-testid="gig-budget-min" />
                <Input type="number" placeholder="Max $" value={form.budget_max} onChange={e => setForm(f => ({ ...f, budget_max: e.target.value }))} data-testid="gig-budget-max" />
                <Select value={form.budget_type} onValueChange={v => setForm(f => ({ ...f, budget_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="negotiable">Negotiable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Timeline</label>
              <Input
                placeholder="e.g., This weekend, Within 2 weeks"
                value={form.timeline}
                onChange={e => setForm(f => ({ ...f, timeline: e.target.value }))}
                data-testid="gig-timeline-input"
              />
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">City</label>
                <Input placeholder="e.g., Washington DC" value={form.location_city} onChange={e => setForm(f => ({ ...f, location_city: e.target.value }))} data-testid="gig-city-input" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Neighborhood</label>
                <Input placeholder="e.g., Southeast" value={form.location_neighborhood} onChange={e => setForm(f => ({ ...f, location_neighborhood: e.target.value }))} data-testid="gig-neighborhood-input" />
              </div>
            </div>

            {/* Submit */}
            <Button type="submit" disabled={submitting} className="w-full" data-testid="submit-gig-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {submitting ? 'Posting...' : editGig ? 'Update Gig' : 'Post Gig'}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
